import React, { createContext, useContext, useMemo, useState } from "react";
import { AssistanceUiAction } from "../../../types/knowledgeArticle";

export const ASSISTANCE_DRAWER_WIDTH_PX = 380;

type UserAssistanceContextValue = {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  anchorEl: HTMLElement | null;
  toggle: (anchor?: HTMLElement | null) => void;
  unreadCount: number;
  setUnreadCount: React.Dispatch<React.SetStateAction<number>>;
  pendingAction: AssistanceUiAction | null;
  requestAction: (action: AssistanceUiAction) => void;
  clearAction: () => void;
};

const UserAssistanceContext = createContext<UserAssistanceContextValue | null>(
  null
);

export function UserAssistanceProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [pendingAction, setPendingAction] = useState<AssistanceUiAction | null>(null);
  const requestAction = React.useCallback((action: AssistanceUiAction) => {
    setPendingAction(action);
  }, []);
  const clearAction = React.useCallback(() => {
    setPendingAction(null);
  }, []);
  const toggle = React.useCallback((anchor?: HTMLElement | null) => {
    if (anchor) {
      setAnchorEl(anchor);
    }
    setOpen((prev) => !prev);
  }, []);

  const value = useMemo<UserAssistanceContextValue>(
    () => ({
      open,
      setOpen,
      anchorEl,
      unreadCount,
      setUnreadCount,
      pendingAction,
      requestAction,
      clearAction,
      toggle,
    }),
    [open, anchorEl, unreadCount, pendingAction, requestAction, clearAction, toggle]
  );

  return (
    <UserAssistanceContext.Provider value={value}>
      {children}
    </UserAssistanceContext.Provider>
  );
}

export function useUserAssistance() {
  const context = useContext(UserAssistanceContext);
  if (!context) {
    throw new Error("useUserAssistance must be used within UserAssistanceProvider");
  }
  return context;
}
